import { c as _c } from "react/compiler-runtime";
import { faChevronDown, faWindowRestore, faCompress } from '@fortawesome/pro-regular-svg-icons';
import { faMinus, faExpand, faArrowToLeft, faArrowToRight, faTimes } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState } from 'react';
import { CroppedText, Text } from 'shared/typography';
import Button from '../button';
import { Box, Flex, Grid } from '../grid';
import ScrollTabs from '../scroll-tabs';
import { Heading } from '../typography';
import Modal_, { ModalProps, ModalWindowProps } from './component';
import { useMediaPredicate as useMedia } from '../hooks/use-media';
import { useTheme } from '@emotion/react';
import DropdownSelect from '../dropdown-select';
import useComponentSize from '../hooks/component-size';
import Tooltip from '../tooltip';
import { useModalContext } from './context';
const nope = () => false;
const sideMargins = [3, 5];
const iconButtonSize = [48, 24];
export const withCloseWarning = (Component: React.ComponentType<any>) =>
// eslint-disable-next-line react/display-name
({
  close,
  hasUnsavedChanges,
  // = nope,
  ...props
}: {
  close: () => void;
  hasUnsavedChanges: boolean | (() => boolean);
} & any) => {
  const [warningVisible, setWarningVisible] = React.useState(false);
  const checkedClose = React.useCallback((skipCheck = false) => {
    function after(dirty) {
      if (dirty) {
        setWarningVisible(true);
      } else {
        if (close) {
          close();
        }
        setWarningVisible(false);
      }
    }
    const result = skipCheck !== true && (typeof hasUnsavedChanges === 'function' ? hasUnsavedChanges() : hasUnsavedChanges);
    if (result?.then) {
      result.then(dirty => after(dirty));
    } else {
      after(result);
    }
  }, [close, hasUnsavedChanges]);
  const dismiss = React.useCallback(() => {
    if (close) {
      close();
    }
    setWarningVisible(false);
  }, [close]);
  return <>
        <Component {...props} close={close && checkedClose} />
        <Modal_ isOpen={warningVisible} close={() => setWarningVisible(false)}>
          {() => <>
              <ModalHeader close={() => setWarningVisible(false)} title="Unsaved changes" type="alert" />

              <Grid p={sideMargins}>
                <CroppedText>
                  You have unsaved changes. Would you like to discard them?
                </CroppedText>

                <Flex justifyContent="flex-end" mt={3}>
                  <Button mr={2} variant="flat" color="gray" onClick={() => setWarningVisible(false)}>
                    Back
                  </Button>

                  <Button color="alert" className="confirm-button" onClick={dismiss}>
                    Discard changes
                  </Button>
                </Flex>
              </Grid>
            </>}
        </Modal_>
      </>;
};
export function CloseButton(t0) {
  const $ = _c(17);
  let onClick;
  let rest;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      ref: t1,
      onClick,
      isDark: t2,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = onClick;
    $[2] = rest;
    $[3] = t1;
    $[4] = t2;
  } else {
    onClick = $[1];
    rest = $[2];
    t1 = $[3];
    t2 = $[4];
  }
  const ref = t1 === undefined ? null : t1;
  const isDark = t2 === undefined ? false : t2;
  const t3 = isDark ? "gray.6" : "gray.1";
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [2, 1];
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const t5 = isDark ? "text" : "white";
  let t6;
  if ($[6] !== t5) {
    t6 = {
      color: t5
    };
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t3 || $[9] !== t6) {
    t7 = {
      cursor: "pointer",
      width: iconButtonSize,
      height: iconButtonSize,
      color: t3,
      fontSize: t4,
      "&:hover, &:active": t6
    };
    $[8] = t3;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <FontAwesomeIcon icon={faTimes} />;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== onClick || $[13] !== ref || $[14] !== rest || $[15] !== t7) {
    t9 = <Tooltip content="Close"><Flex justifyContent="center" alignItems="center" ref={ref} as="button" type="button" className="close" onClick={onClick} tabIndex={-1} borderRadius={1} sx={t7} {...rest}>{t8}</Flex></Tooltip>;
    $[12] = onClick;
    $[13] = ref;
    $[14] = rest;
    $[15] = t7;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  return t9;
}
interface ModalHeaderProps {
  close?: () => void;
  title: React.ReactNode;
  tabs?: React.ReactNode;
  rightCornerTrigger?: React.ReactNode;
  type?: string;
  leftCorner?: React.ReactNode;
  rightCorner?: React.ReactNode;
  subtitle?: React.ReactNode;
  additionalIconActions?: React.JSX.Element | string;
}
const sizeToString = (size: ModalWindowProps['size']) => {
  const sizeMap = {
    tiny: 'Centre window',
    small: 'Centre window',
    medium: 'Centre window',
    large: 'Centre window',
    xlarge: 'Centre window',
    full: 'Full screen',
    minimized: 'Minimize',
    right: 'Dock right',
    left: 'Dock left'
  };
  return sizeMap[size] || '';
};
const sizeToIcon = (size: ModalWindowProps['size'], currentSize: ModalWindowProps['size']) => {
  const isDocked = ['left', 'right'].includes(currentSize);
  const sizeMap = {
    tiny: isDocked ? faWindowRestore : faCompress,
    small: isDocked ? faWindowRestore : faCompress,
    medium: isDocked ? faWindowRestore : faCompress,
    large: isDocked ? faWindowRestore : faCompress,
    xlarge: isDocked ? faWindowRestore : faCompress,
    full: faExpand,
    minimized: faMinus,
    right: faArrowToRight,
    left: faArrowToLeft
  };
  return sizeMap[size] || faCompress;
};
const ModalHeader_ = t0 => {
  const $ = _c(68);
  const {
    close,
    title,
    tabs: t1,
    rightCornerTrigger: t2,
    type: t3,
    leftCorner: t4,
    rightCorner: t5,
    subtitle: t6,
    additionalIconActions: t7
  } = t0;
  const tabs = t1 === undefined ? null : t1;
  const rightCornerTrigger = t2 === undefined ? null : t2;
  t3 === undefined ? "" : t3;
  const leftCorner = t4 === undefined ? null : t4;
  const rightCorner = t5 === undefined ? null : t5;
  const subtitle = t6 === undefined ? null : t6;
  const additionalIconActions = t7 === undefined ? null : t7;
  const {
    size,
    sizeOptions: t8,
    changeSize: t9
  } = useModalContext();
  let t10;
  if ($[0] !== t8) {
    t10 = t8 === undefined ? [] : t8;
    $[0] = t8;
    $[1] = t10;
  } else {
    t10 = $[1];
  }
  const requestedSizeOptions = t10;
  const changeSize = t9 === undefined ? null : t9;
  const ref = React.useRef(null);
  const {
    width
  } = useComponentSize(ref);
  const [rightCornerOpen, setRightCornerOpen] = useState(false);
  const theme = useTheme();
  const isSmall = width < 768;
  let t11;
  let t12;
  if ($[2] !== isSmall || $[3] !== rightCornerOpen) {
    t11 = () => {
      if (!isSmall && rightCornerOpen) {
        setRightCornerOpen(false);
      }
    };
    t12 = [isSmall, rightCornerOpen];
    $[2] = isSmall;
    $[3] = rightCornerOpen;
    $[4] = t11;
    $[5] = t12;
  } else {
    t11 = $[4];
    t12 = $[5];
  }
  React.useEffect(t11, t12);
  const isXLarge = useMedia(theme.mediaQueries.xLargeUp);
  let t13;
  if ($[6] !== isXLarge || $[7] !== requestedSizeOptions || $[8] !== size) {
    let t14;
    if ($[10] !== isXLarge || $[11] !== size) {
      t14 = requestedSize => {
        if (requestedSize == size) {
          return false;
        }
        if (isXLarge) {
          return true;
        }
        return !["left", "right"].includes(requestedSize);
      };
      $[10] = isXLarge;
      $[11] = size;
      $[12] = t14;
    } else {
      t14 = $[12];
    }
    t13 = requestedSizeOptions.filter(t14);
    $[6] = isXLarge;
    $[7] = requestedSizeOptions;
    $[8] = size;
    $[9] = t13;
  } else {
    t13 = $[9];
  }
  const sizeOptions = t13;
  let t14;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      containerType: "inline-size"
    };
    $[13] = t14;
  } else {
    t14 = $[13];
  }
  let t15;
  let t16;
  let t17;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = [0, 4];
    t16 = [0, 1];
    t17 = [0, 1];
    $[14] = t15;
    $[15] = t16;
    $[16] = t17;
  } else {
    t15 = $[14];
    t16 = $[15];
    t17 = $[16];
  }
  let t18;
  if ($[17] !== changeSize || $[18] !== size || $[19] !== sizeOptions) {
    t18 = changeSize && sizeOptions.length > 0 && <Flex>{sizeOptions.map((option, idx) => <Tooltip key={idx} content={sizeToString(option)}><Flex width={iconButtonSize} height={iconButtonSize} justifyContent="center" alignItems="center" as="button" sx={{
          color: "gray.6",
          "&:hover": {
            color: "text"
          },
          fontSize: [2, 1]
        }} onClick={() => changeSize(option)}><FontAwesomeIcon icon={sizeToIcon(option, size)} /></Flex></Tooltip>)}</Flex>;
    $[17] = changeSize;
    $[18] = size;
    $[19] = sizeOptions;
    $[20] = t18;
  } else {
    t18 = $[20];
  }
  let t19;
  if ($[21] !== t18) {
    t19 = <Box>{t18}</Box>;
    $[21] = t18;
    $[22] = t19;
  } else {
    t19 = $[22];
  }
  let t20;
  if ($[23] !== close) {
    t20 = close && <CloseButton isDark={true} onClick={close} />;
    $[23] = close;
    $[24] = t20;
  } else {
    t20 = $[24];
  }
  let t21;
  if ($[25] !== additionalIconActions || $[26] !== t20) {
    t21 = <Flex>{additionalIconActions}{t20}</Flex>;
    $[25] = additionalIconActions;
    $[26] = t20;
    $[27] = t21;
  } else {
    t21 = $[27];
  }
  let t22;
  if ($[28] !== t19 || $[29] !== t21) {
    t22 = <Flex justifyContent="space-between" alignItems="center" px={t15} py={t16} borderBottomWidth={t17} borderBottomColor="borderColor" borderBottomStyle="solid">{t19}{t21}</Flex>;
    $[28] = t19;
    $[29] = t21;
    $[30] = t22;
  } else {
    t22 = $[30];
  }
  const t23 = subtitle ? "flex-start" : "center";
  let t24;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = [0, 3];
    $[31] = t24;
  } else {
    t24 = $[31];
  }
  let t25;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = {
      gridGap: 2
    };
    $[32] = t25;
  } else {
    t25 = $[32];
  }
  let t26;
  if ($[33] !== leftCorner) {
    t26 = leftCorner && <Box flexShrink={0} minWidth={0}>{leftCorner && <Flex alignItems="center">{leftCorner}</Flex>}</Box>;
    $[33] = leftCorner;
    $[34] = t26;
  } else {
    t26 = $[34];
  }
  let t27;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = {
      overflow: "hidden",
      display: "-webkit-box",
      WebkitLineClamp: 1,
      WebkitBoxOrient: "vertical",
      overflowWrap: "break-word"
    };
    $[35] = t27;
  } else {
    t27 = $[35];
  }
  let t28;
  if ($[36] !== title) {
    t28 = <Flex flexGrow={1}><Heading fontSize={4} fontWeight="bold" as="div" sx={t27}>{title}</Heading></Flex>;
    $[36] = title;
    $[37] = t28;
  } else {
    t28 = $[37];
  }
  let t29;
  if ($[38] !== subtitle || $[39] !== t28) {
    t29 = <Flex flexDirection="column">{t28}{subtitle}</Flex>;
    $[38] = subtitle;
    $[39] = t28;
    $[40] = t29;
  } else {
    t29 = $[40];
  }
  let t30;
  if ($[41] !== t26 || $[42] !== t29) {
    t30 = <Flex width="100%" height="auto" alignItems="center" flex="1 1 auto" minWidth={0}>{t26}{t29}</Flex>;
    $[41] = t26;
    $[42] = t29;
    $[43] = t30;
  } else {
    t30 = $[43];
  }
  let t31;
  if ($[44] !== rightCorner || $[45] !== rightCornerTrigger) {
    t31 = rightCorner && <Box sx={{
      display: rightCornerTrigger ? "none" : "flex",
      "@container (min-width: 768px)": {
        display: "flex"
      }
    }}>{rightCorner}</Box>;
    $[44] = rightCorner;
    $[45] = rightCornerTrigger;
    $[46] = t31;
  } else {
    t31 = $[46];
  }
  let t32;
  if ($[47] !== rightCornerOpen || $[48] !== rightCornerTrigger) {
    t32 = rightCornerTrigger && <Box><Box as="button" sx={{
        background: "none",
        border: "none",
        padding: 0,
        margin: 0,
        display: "flex",
        alignItems: "center",
        cursor: "pointer"
      }} onClick={() => setRightCornerOpen(_temp)}><Flex alignItems="center" sx={{
          gridGap: 2,
          display: "flex",
          "@container (min-width: 768px)": {
            display: "none"
          }
        }} width="100%">{rightCornerTrigger}<Box sx={{
            transition: "transform 0.2s",
            color: "gray.6",
            transform: rightCornerOpen ? "rotate(180deg)" : "rotate(0deg)"
          }}><FontAwesomeIcon size="sm" icon={faChevronDown} /></Box></Flex></Box></Box>;
    $[47] = rightCornerOpen;
    $[48] = rightCornerTrigger;
    $[49] = t32;
  } else {
    t32 = $[49];
  }
  let t33;
  if ($[50] !== t31 || $[51] !== t32) {
    t33 = <Flex alignItems="center" minWidth={0} flexShrink={0}>{t31}{t32}</Flex>;
    $[50] = t31;
    $[51] = t32;
    $[52] = t33;
  } else {
    t33 = $[52];
  }
  let t34;
  if ($[53] !== t23 || $[54] !== t30 || $[55] !== t33) {
    t34 = <Flex className="header" justifyContent="space-between" alignItems={t23} mx={sideMargins} mt={t24} mb={3} flexWrap="nowrap" position="relative" sx={t25}>{t30}{t33}</Flex>;
    $[53] = t23;
    $[54] = t30;
    $[55] = t33;
    $[56] = t34;
  } else {
    t34 = $[56];
  }
  let t35;
  if ($[57] !== tabs) {
    t35 = tabs && <Box flex="1 1 auto" overflow="hidden" alignSelf="flex-end" mx={[0, 5]}>{tabs}</Box>;
    $[57] = tabs;
    $[58] = t35;
  } else {
    t35 = $[58];
  }
  let t36;
  if ($[59] !== rightCorner || $[60] !== rightCornerOpen || $[61] !== rightCornerTrigger) {
    t36 = rightCornerTrigger && <Box flex="1 1 auto" alignSelf="flex-end" backgroundColor="bodyBackground" sx={{
      display: "block",
      "@container (min-width: 768px)": {
        display: "none"
      },
      height: rightCornerOpen ? "auto" : 0,
      overflow: "hidden",
      paddingTop: rightCornerOpen ? 3 : 0,
      paddingBottom: rightCornerOpen ? 3 : 0,
      paddingX: 4,
      transition: "height 0.8s cubic-bezier(.4,0,.2,1), padding-top 0.4s cubic-bezier(.4,0,.2,1), padding-bottom 0.4s cubic-bezier(.4,0,.2,1)"
    }}>{rightCorner}</Box>;
    $[59] = rightCorner;
    $[60] = rightCornerOpen;
    $[61] = rightCornerTrigger;
    $[62] = t36;
  } else {
    t36 = $[62];
  }
  let t37;
  if ($[63] !== t22 || $[64] !== t34 || $[65] !== t35 || $[66] !== t36) {
    t37 = <Box sx={t14} ref={ref}>{t22}{t34}{t35}{t36}</Box>;
    $[63] = t22;
    $[64] = t34;
    $[65] = t35;
    $[66] = t36;
    $[67] = t37;
  } else {
    t37 = $[67];
  }
  return t37;
};
export const ModalHeader = withCloseWarning(ModalHeader_);
const Modal = withCloseWarning(Modal_);
export default Modal;
interface ConfirmModalProps {
  isOpen: boolean;
  title: string;
  subtitle?: string;
  close?: () => void;
  className?: string;
  type?: string;
  onConfirm?: () => Promise<any> | void;
  confirmText?: string;
  dismissText?: string;
  leftOfButtons?: string | React.ReactElement | React.JSX.Element;
  children;
  zIndexDelta?: number;
}
export const ConfirmModal = t0 => {
  const $ = _c(20);
  const {
    isOpen,
    title,
    subtitle,
    close,
    className: t1,
    type: t2,
    zIndexDelta: t3,
    onConfirm: t4,
    confirmText: t5,
    dismissText: t6,
    leftOfButtons: t7,
    children
  } = t0;
  const className = t1 === undefined ? "" : t1;
  const type = t2 === undefined ? "" : t2;
  const zIndexDelta = t3 === undefined ? 0 : t3;
  const onConfirm = t4 === undefined ? Promise.resolve : t4;
  const confirmText = t5 === undefined ? "" : t5;
  const dismissText = t6 === undefined ? "" : t6;
  const leftOfButtons = t7 === undefined ? null : t7;
  const [isLoading, setIsLoading] = React.useState(false);
  let t8;
  let t9;
  if ($[0] !== isOpen) {
    t8 = () => {
      if (!isOpen) {
        setIsLoading(false);
      }
    };
    t9 = [isOpen];
    $[0] = isOpen;
    $[1] = t8;
    $[2] = t9;
  } else {
    t8 = $[1];
    t9 = $[2];
  }
  React.useEffect(t8, t9);
  const t10 = `${className} ${type}`;
  let t11;
  if ($[3] !== children || $[4] !== close || $[5] !== confirmText || $[6] !== dismissText || $[7] !== isLoading || $[8] !== leftOfButtons || $[9] !== onConfirm || $[10] !== subtitle || $[11] !== title || $[12] !== type) {
    t11 = () => <><ModalHeader_ close={close} title={title} subtitle={subtitle} type={type} /><Grid p={sideMargins}><Box>{children}</Box><Flex justifyContent="space-between" mt={3}><Box>{leftOfButtons}</Box><Flex>{dismissText && <Button mr={2} variant="flat" color="gray" onClick={close}>{dismissText || "No"}</Button>}<Button color={type || "primary"} className="confirm-button" loading={isLoading} disabled={isLoading} onClick={() => {
              const result = onConfirm();
              if (result && result.then) {
                setIsLoading(true);
                result.then(() => {
                  setIsLoading(false);
                }).catch(() => {
                  setIsLoading(false);
                });
              }
            }}>{confirmText || "Yes"}</Button></Flex></Flex></Grid></>;
    $[3] = children;
    $[4] = close;
    $[5] = confirmText;
    $[6] = dismissText;
    $[7] = isLoading;
    $[8] = leftOfButtons;
    $[9] = onConfirm;
    $[10] = subtitle;
    $[11] = title;
    $[12] = type;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  let t12;
  if ($[14] !== close || $[15] !== isOpen || $[16] !== t10 || $[17] !== t11 || $[18] !== zIndexDelta) {
    t12 = <Modal isOpen={isOpen} close={close} className={t10} zIndexDelta={zIndexDelta}>{t11}</Modal>;
    $[14] = close;
    $[15] = isOpen;
    $[16] = t10;
    $[17] = t11;
    $[18] = zIndexDelta;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  return t12;
};
export const StandardModal = t0 => {
  const $ = _c(27);
  let children;
  let close;
  let isOpen;
  let rest;
  let subtitle;
  let t1;
  let t2;
  let t3;
  let t4;
  let title;
  if ($[0] !== t0) {
    ({
      isOpen,
      title,
      subtitle,
      close,
      leftCorner: t1,
      rightCorner: t2,
      additionalIconActions: t3,
      hasUnsavedChanges: t4,
      children,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = close;
    $[3] = isOpen;
    $[4] = rest;
    $[5] = subtitle;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
    $[10] = title;
  } else {
    children = $[1];
    close = $[2];
    isOpen = $[3];
    rest = $[4];
    subtitle = $[5];
    t1 = $[6];
    t2 = $[7];
    t3 = $[8];
    t4 = $[9];
    title = $[10];
  }
  const leftCorner = t1 === undefined ? null : t1;
  const rightCorner = t2 === undefined ? null : t2;
  const additionalIconActions = t3 === undefined ? null : t3;
  const hasUnsavedChanges = t4 === undefined ? nope : t4;
  let t5;
  if ($[11] !== additionalIconActions || $[12] !== children || $[13] !== leftCorner || $[14] !== rest.changeSize || $[15] !== rest.size || $[16] !== rest.sizeOptions || $[17] !== rightCorner || $[18] !== subtitle || $[19] !== title) {
    t5 = props => <><ModalHeader title={title} subtitle={subtitle} close={props.close} leftCorner={leftCorner} rightCorner={rightCorner} additionalIconActions={additionalIconActions} changeSize={rest.changeSize} sizeOptions={rest.sizeOptions} size={rest.size} /><Grid className="standard-modal-content" px={sideMargins} pb={5} pt={0}>{children({
          ...props
        })}</Grid></>;
    $[11] = additionalIconActions;
    $[12] = children;
    $[13] = leftCorner;
    $[14] = rest.changeSize;
    $[15] = rest.size;
    $[16] = rest.sizeOptions;
    $[17] = rightCorner;
    $[18] = subtitle;
    $[19] = title;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  let t6;
  if ($[21] !== close || $[22] !== hasUnsavedChanges || $[23] !== isOpen || $[24] !== rest || $[25] !== t5) {
    t6 = <Modal isOpen={isOpen} close={close} hasUnsavedChanges={hasUnsavedChanges} {...rest}>{t5}</Modal>;
    $[21] = close;
    $[22] = hasUnsavedChanges;
    $[23] = isOpen;
    $[24] = rest;
    $[25] = t5;
    $[26] = t6;
  } else {
    t6 = $[26];
  }
  return t6;
};
export const TabModal = t0 => {
  const $ = _c(36);
  let activeItem;
  let children;
  let className;
  let close;
  let isOpen;
  let items;
  let rest;
  let rightCorner;
  let rightCornerTrigger;
  let setActiveItem;
  let subtitle;
  let t1;
  let t2;
  let title;
  if ($[0] !== t0) {
    ({
      isOpen,
      title,
      close,
      hasUnsavedChanges: t1,
      children,
      activeItem,
      items,
      setActiveItem,
      className,
      subtitle,
      rightCorner,
      rightCornerTrigger,
      additionalIconActions: t2,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = activeItem;
    $[2] = children;
    $[3] = className;
    $[4] = close;
    $[5] = isOpen;
    $[6] = items;
    $[7] = rest;
    $[8] = rightCorner;
    $[9] = rightCornerTrigger;
    $[10] = setActiveItem;
    $[11] = subtitle;
    $[12] = t1;
    $[13] = t2;
    $[14] = title;
  } else {
    activeItem = $[1];
    children = $[2];
    className = $[3];
    close = $[4];
    isOpen = $[5];
    items = $[6];
    rest = $[7];
    rightCorner = $[8];
    rightCornerTrigger = $[9];
    setActiveItem = $[10];
    subtitle = $[11];
    t1 = $[12];
    t2 = $[13];
    title = $[14];
  }
  const hasUnsavedChanges = t1 === undefined ? nope : t1;
  const additionalIconActions = t2 === undefined ? null : t2;
  const t3 = `tab-modal ${className}`;
  let t4;
  if ($[15] !== activeItem || $[16] !== additionalIconActions || $[17] !== children || $[18] !== close || $[19] !== hasUnsavedChanges || $[20] !== items || $[21] !== rest.changeSize || $[22] !== rest.sizeOptions || $[23] !== rightCorner || $[24] !== rightCornerTrigger || $[25] !== setActiveItem || $[26] !== subtitle || $[27] !== title) {
    t4 = props => <><ModalHeader title={title} close={close} hasUnsavedChanges={hasUnsavedChanges} rightCorner={rightCorner} rightCornerTrigger={rightCornerTrigger} subtitle={subtitle} additionalIconActions={additionalIconActions} changeSize={rest.changeSize} sizeOptions={rest.sizeOptions} tabs={<ScrollTabs rtl={true} activeItem={activeItem} items={items} setActiveItem={setActiveItem} />} /><Grid p={sideMargins}>{children({
          ...props,
          close
        })}</Grid></>;
    $[15] = activeItem;
    $[16] = additionalIconActions;
    $[17] = children;
    $[18] = close;
    $[19] = hasUnsavedChanges;
    $[20] = items;
    $[21] = rest.changeSize;
    $[22] = rest.sizeOptions;
    $[23] = rightCorner;
    $[24] = rightCornerTrigger;
    $[25] = setActiveItem;
    $[26] = subtitle;
    $[27] = title;
    $[28] = t4;
  } else {
    t4 = $[28];
  }
  let t5;
  if ($[29] !== close || $[30] !== hasUnsavedChanges || $[31] !== isOpen || $[32] !== rest || $[33] !== t3 || $[34] !== t4) {
    t5 = <Modal isOpen={isOpen} close={close} className={t3} hasUnsavedChanges={hasUnsavedChanges} {...rest}>{t4}</Modal>;
    $[29] = close;
    $[30] = hasUnsavedChanges;
    $[31] = isOpen;
    $[32] = rest;
    $[33] = t3;
    $[34] = t4;
    $[35] = t5;
  } else {
    t5 = $[35];
  }
  return t5;
};
function _temp(v) {
  return !v;
}